import React, { useState, useEffect, useContext } from 'react';
import { fmtDate as _fmtDate, humanizeDates } from '@/lib/dates';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { AuthContext, API } from '@/App';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { FileText, Plus, Edit, Trash2, Download, Filter, RefreshCw, ArrowLeft, Search, Eye, Mail } from 'lucide-react';

const AdminWorkOrderManagement = () => {
  const { token, user } = useContext(AuthContext);
  const canPurge = user?.role === 'owner' || (user?.capabilities || []).includes('admin.controls');
  const navigate = useNavigate();
  const [workOrders, setWorkOrders] = useState([]);
  const [filteredOrders, setFilteredOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Filter states
  const [filters, setFilters] = useState({
    dateFrom: '',
    dateTo: '',
    customer: '',
    location: '',
    tradeType: 'all',
    priority: 'all',
    billable: 'all',
    status: 'all',
    workOrderNumber: ''
  });

  // Modal states
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [orderToDelete, setOrderToDelete] = useState(null);
  const [showPreviewDialog, setShowPreviewDialog] = useState(false);
  const [orderToPreview, setOrderToPreview] = useState(null);
  const [showCloseDialog, setShowCloseDialog] = useState(false);
  const [internalNotes, setInternalNotes] = useState('');

  const tradeTypes = [
    'Janitorial', 'Event Support', 'Landscape', 'Security', 
    'Sweeping', 'General Maintenance', 'Special Projects'
  ];

  const priorities = [
    { value: 'P1', label: 'P1: Within 2 hours' },
    { value: 'P2', label: 'P2: Same evening' },
    { value: 'P3', label: 'P3: Same week' },
    { value: 'P4', label: 'P4: Within 30 days' }
  ];

  useEffect(() => {
    fetchWorkOrders();
  }, []);

  useEffect(() => {
    applyFilters();
  }, [filters, workOrders]);

  const fetchWorkOrders = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/work-orders/admin/all`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setWorkOrders(response.data);
    } catch (error) {
      toast.error('Failed to load work orders');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    let filtered = [...workOrders];

    // Date range filter
    if (filters.dateFrom) {
      filtered = filtered.filter(order => 
        new Date(order.date_received) >= new Date(filters.dateFrom)
      );
    }
    if (filters.dateTo) {
      filtered = filtered.filter(order => 
        new Date(order.date_received) <= new Date(filters.dateTo)
      );
    }

    // Text filters
    if (filters.customer) {
      filtered = filtered.filter(order => 
        order.customer_name?.toLowerCase().includes(filters.customer.toLowerCase())
      );
    }
    if (filters.location) {
      filtered = filtered.filter(order => 
        order.service_location?.toLowerCase().includes(filters.location.toLowerCase())
      );
    }
    if (filters.workOrderNumber) {
      filtered = filtered.filter(order => 
        order.tfs_work_order_number?.toLowerCase().includes(filters.workOrderNumber.toLowerCase())
      );
    }

    // Dropdown filters
    if (filters.tradeType && filters.tradeType !== 'all') {
      filtered = filtered.filter(order => order.trade_type === filters.tradeType);
    }
    if (filters.priority && filters.priority !== 'all') {
      filtered = filtered.filter(order => order.priority_level === filters.priority);
    }
    if (filters.billable && filters.billable !== 'all') {
      const isBillable = filters.billable === 'yes';
      filtered = filtered.filter(order => order.billable === isBillable);
    }
    if (filters.status && filters.status !== 'all') {
      filtered = filtered.filter(order => order.status === filters.status);
    }

    setFilteredOrders(filtered);
  };

  const handleCreateWorkOrder = () => {
    navigate('/admin/work-orders/new');
  };

  const handleEditWorkOrder = (workOrder) => {
    navigate('/admin/work-orders/edit', { state: { editData: workOrder } });
  };

  const handleDeleteWorkOrder = (workOrder) => {
    setOrderToDelete(workOrder);
    setShowDeleteDialog(true);
  };

  const handleViewWorkOrder = (workOrder) => {
    navigate(`/admin/work-orders/detail?id=${workOrder.id}`);
  };

  const handleDownloadPDF = async (workOrder) => {
    try {
      toast.info('Preparing PDF download...');
      
      // Use hidden iframe to trigger download
      // Backend returns Content-Disposition: attachment to force download
      const pdfUrl = `${API}/work-orders/${workOrder.id}/pdf?auth_token=${encodeURIComponent(token)}`;
      
      const iframe = document.createElement('iframe');
      iframe.style.display = 'none';
      iframe.src = pdfUrl;
      
      document.body.appendChild(iframe);
      
      toast.success(`PDF download started: WorkOrder_${workOrder.tfs_work_order_number}.pdf`);
      
      // Cleanup iframe after download initiates
      setTimeout(() => {
        if (document.body.contains(iframe)) {
          document.body.removeChild(iframe);
        }
      }, 5000);
      
    } catch (error) {
      console.error('PDF download error:', error);
      toast.error(`Failed to download PDF: ${error.message}`);
    }
  };

  const handleEmailPDF = async (workOrder) => {
    try {
      const response = await axios.post(
        `${API}/work-orders/${workOrder.id}/send-pdf`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success(`PDF email logged for ${workOrder.tfs_work_order_number}`);
    } catch (error) {
      toast.error('Failed to send PDF email');
    }
  };

  const confirmDelete = async () => {
    try {
      await axios.delete(`${API}/work-orders/${orderToDelete.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Work order deleted successfully');
      setShowDeleteDialog(false);
      setOrderToDelete(null);
      fetchWorkOrders();
    } catch (error) {
      toast.error('Failed to delete work order');
      setShowDeleteDialog(false);
      setOrderToDelete(null);
    }
  };

  const handleCloseWorkOrder = () => {
    setShowCloseDialog(true);
  };

  const confirmCloseWorkOrder = async () => {
    try {
      await axios.put(
        `${API}/work-orders/${orderToPreview.id}`,
        {
          status: 'Closed by Admin',
          admin_closed: true,
          admin_closed_by: 'Admin',
          admin_closed_at: new Date().toISOString()
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Work order closed successfully');
      setShowCloseDialog(false);
      setShowPreviewDialog(false);
      fetchWorkOrders();
    } catch (error) {
      toast.error('Failed to close work order');
      setShowCloseDialog(false);
    }
  };

  const handleSaveInternalNotes = async () => {
    try {
      await axios.put(
        `${API}/work-orders/${orderToPreview.id}`,
        { internal_notes: internalNotes },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Internal notes saved');
      fetchWorkOrders();
    } catch (error) {
      toast.error('Failed to save internal notes');
    }
  };

  const handleExport = async (format) => {
    try {
      toast.info(`Preparing ${format.toUpperCase()} download...`);
      
      // Build export URL with filters and auth token
      const params = new URLSearchParams({
        format,
        auth_token: token,
        ...filters
      });
      
      const exportUrl = `${API}/work-orders/admin/export?${params.toString()}`;
      
      // Use hidden iframe to trigger download
      const iframe = document.createElement('iframe');
      iframe.style.display = 'none';
      iframe.src = exportUrl;
      
      document.body.appendChild(iframe);
      
      const filename = `work-orders-${new Date().toISOString().split('T')[0]}.${format}`;
      toast.success(`${format.toUpperCase()} download started: ${filename}`);
      
      // Cleanup iframe after download initiates
      setTimeout(() => {
        if (document.body.contains(iframe)) {
          document.body.removeChild(iframe);
        }
      }, 5000);
      
    } catch (error) {
      console.error('Export error:', error);
      toast.error(`Failed to export ${format.toUpperCase()}: ${error.message}`);
    }
  };

  const getPriorityColor = (priority) => {
    const colors = {
      'P1': 'bg-red-100 text-red-800',
      'P2': 'bg-orange-100 text-orange-800',
      'P3': 'bg-yellow-100 text-yellow-800',
      'P4': 'bg-green-100 text-green-800'
    };
    return colors[priority] || 'bg-gray-100 text-gray-800';
  };

  const clearFilters = () => {
    setFilters({
      dateFrom: '',
      dateTo: '',
      customer: '',
      location: '',
      tradeType: 'all',
      priority: 'all',
      billable: 'all',
      status: 'all',
      workOrderNumber: ''
    });
  };

  if (loading) {
    return (
      <div>
        
        <div className="flex items-center justify-center py-12">
          <RefreshCw className="h-6 w-6 animate-spin text-gray-400" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      
      
      <div className="p-6">
        <div className="flex justify-between items-center mb-6">
          <div>
            <h2 className="text-2xl font-bold text-gray-800">Work Order Database</h2>
            <p className="text-gray-600 mt-1">Manage all TFS work orders</p>
          </div>
        <div className="flex space-x-2">
          <Button onClick={() => handleExport('xlsx')} variant="outline">
            <Download className="h-4 w-4 mr-2" />
            Export Excel
          </Button>
          <Button onClick={() => handleExport('csv')} variant="outline">
            <Download className="h-4 w-4 mr-2" />
            Export CSV
          </Button>
          <Button onClick={handleCreateWorkOrder} className="bg-blue-600 hover:bg-blue-700">
            <Plus className="h-4 w-4 mr-2" />
            Create Work Order
          </Button>
        </div>
      </div>

      {/* Filters */}
      <Card className="mb-6">
        <CardHeader>
          <div className="flex justify-between items-center">
            <CardTitle className="text-lg">Filters</CardTitle>
            <Button variant="outline" size="sm" onClick={clearFilters}>
              Clear All
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <label className="text-sm font-medium">Date From</label>
              <Input
                type="date"
                value={filters.dateFrom}
                onChange={(e) => setFilters({...filters, dateFrom: e.target.value})}
              />
            </div>
            <div>
              <label className="text-sm font-medium">Date To</label>
              <Input
                type="date"
                value={filters.dateTo}
                onChange={(e) => setFilters({...filters, dateTo: e.target.value})}
              />
            </div>
            <div>
              <label className="text-sm font-medium">Customer</label>
              <Input
                placeholder="Search customer..."
                value={filters.customer}
                onChange={(e) => setFilters({...filters, customer: e.target.value})}
              />
            </div>
            <div>
              <label className="text-sm font-medium">Location</label>
              <Input
                placeholder="Search location..."
                value={filters.location}
                onChange={(e) => setFilters({...filters, location: e.target.value})}
              />
            </div>
            <div>
              <label className="text-sm font-medium">Work Order Number</label>
              <Input
                placeholder="TFS-20241124-001"
                value={filters.workOrderNumber}
                onChange={(e) => setFilters({...filters, workOrderNumber: e.target.value})}
              />
            </div>
            <div>
              <label className="text-sm font-medium">Trade Type</label>
              <Select value={filters.tradeType} onValueChange={(value) => setFilters({...filters, tradeType: value})}>
                <SelectTrigger>
                  <SelectValue placeholder="All trades" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Trades</SelectItem>
                  {tradeTypes.map(trade => (
                    <SelectItem key={trade} value={trade}>{trade}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-sm font-medium">Priority</label>
              <Select value={filters.priority} onValueChange={(value) => setFilters({...filters, priority: value})}>
                <SelectTrigger>
                  <SelectValue placeholder="All priorities" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Priorities</SelectItem>
                  {priorities.map(priority => (
                    <SelectItem key={priority.value} value={priority.value}>{priority.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-sm font-medium">Billable</label>
              <Select value={filters.billable} onValueChange={(value) => setFilters({...filters, billable: value})}>
                <SelectTrigger>
                  <SelectValue placeholder="All" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All</SelectItem>
                  <SelectItem value="yes">Yes</SelectItem>
                  <SelectItem value="no">No</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-sm font-medium">Status</label>
              <Select value={filters.status} onValueChange={(value) => setFilters({...filters, status: value})}>
                <SelectTrigger>
                  <SelectValue placeholder="All statuses" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All</SelectItem>
                  <SelectItem value="Open">Open</SelectItem>
                  <SelectItem value="In Progress">In Progress</SelectItem>
                  <SelectItem value="Completed">Completed</SelectItem>
                  <SelectItem value="Closed by Admin">Closed by Admin</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="mt-4 text-sm text-gray-600">
            Showing {filteredOrders.length} of {workOrders.length} work orders
          </div>
        </CardContent>
      </Card>

      {/* Work Orders Table */}
      <Card>
        <CardContent className="p-0">
          {filteredOrders.length === 0 ? (
            <div className="text-center py-12">
              <FileText className="h-12 w-12 mx-auto mb-4 text-gray-400" />
              <p className="text-gray-500">No work orders found</p>
            </div>
          ) : (
            <div className="border rounded-md">
              <div className="overflow-x-auto">
                <table className="w-full text-xs border-collapse" style={{minWidth: '900px'}}>
                  <thead>
                    <tr className="border-b bg-gray-50">
                      <th className="text-left py-1.5 px-1 font-medium whitespace-nowrap" style={{width: '85px'}}>WO #</th>
                      <th className="text-left py-1.5 px-1 font-medium whitespace-nowrap" style={{width: '135px'}}>Job</th>
                      <th className="text-left py-1.5 px-1 font-medium whitespace-nowrap" style={{width: '115px'}}>Customer</th>
                      <th className="text-left py-1.5 px-1 font-medium whitespace-nowrap" style={{width: '95px'}}>Location</th>
                      <th className="text-left py-1.5 px-1 font-medium whitespace-nowrap" style={{width: '75px'}}>Trade</th>
                      <th className="text-left py-1.5 px-1 font-medium whitespace-nowrap" style={{width: '40px'}}>Pri</th>
                      <th className="text-left py-1.5 px-1 font-medium whitespace-nowrap" style={{width: '85px'}}>User</th>
                      <th className="text-left py-1.5 px-1 font-medium whitespace-nowrap" style={{width: '65px'}}>Date</th>
                      <th className="text-center py-1.5 px-1 font-medium whitespace-nowrap" style={{width: '30px'}}>$</th>
                      <th className="text-left py-1.5 px-1 font-medium whitespace-nowrap" style={{width: '65px'}}>Status</th>
                      <th className="text-right py-1.5 px-1 font-medium whitespace-nowrap sticky right-0 bg-gray-50" style={{width: '105px'}}>Actions</th>
                    </tr>
                  </thead>
                <tbody>
                  {filteredOrders.map((order) => (
                    <tr key={order.id} className="border-b hover:bg-gray-50">
                      <td className="py-1 px-0.5 font-semibold text-red-600 truncate whitespace-nowrap" title={order.tfs_work_order_number} style={{maxWidth: '85px'}}>
                        {order.tfs_work_order_number}
                      </td>
                      <td className="py-1 px-0.5 font-medium truncate" title={humanizeDates(order.job_name)} style={{maxWidth: '135px'}}>
                        {humanizeDates(order.job_name)}
                      </td>
                      <td className="py-1 px-0.5 truncate" title={order.customer_name} style={{maxWidth: '115px'}}>
                        {order.customer_name}
                      </td>
                      <td className="py-1 px-0.5 truncate" title={order.service_location} style={{maxWidth: '95px'}}>
                        {order.service_location}
                      </td>
                      <td className="py-1 px-0.5 truncate" style={{maxWidth: '75px'}}>
                        {order.trade_type?.substring(0, 7)}
                      </td>
                      <td className="py-1 px-0.5 text-center">
                        <span className={`${getPriorityColor(order.priority_level)} text-xs font-bold`}>
                          {order.priority_level}
                        </span>
                      </td>
                      <td className="py-1 px-0.5 truncate" title={order.assigned_to_name} style={{maxWidth: '85px'}}>
                        {order.assigned_to_name?.split(' ')[0] || '-'}
                      </td>
                      <td className="py-1 px-0.5 whitespace-nowrap" style={{fontSize: '10px'}}>
                        {_fmtDate(order.required_service_date)}
                      </td>
                      <td className="py-1 px-0.5 text-center">
                        <span className={order.billable ? 'text-green-600 font-bold' : 'text-gray-400'}>
                          {order.billable ? "✓" : "○"}
                        </span>
                      </td>
                      <td className="py-1 px-0.5 truncate" style={{maxWidth: '65px'}}>
                        <span className={`text-xs ${order.status === 'Completed' ? 'text-green-600 font-semibold' : 'text-gray-600'}`}>
                          {order.status.substring(0, 8)}
                        </span>
                      </td>
                      <td className="py-1 px-0.5 sticky right-0 bg-white" style={{width: '105px'}}>
                        <div className="flex gap-0.5 justify-end">
                          <button
                            onClick={() => handleViewWorkOrder(order)}
                            title="View"
                            className="h-6 w-6 p-0 inline-flex items-center justify-center rounded hover:bg-blue-100 transition-colors"
                          >
                            <Eye className="h-3 w-3" />
                          </button>
                          <button
                            onClick={() => handleEditWorkOrder(order)}
                            title="Edit"
                            className="h-6 w-6 p-0 inline-flex items-center justify-center rounded hover:bg-amber-100 transition-colors"
                          >
                            <Edit className="h-3 w-3" />
                          </button>
                          {canPurge && (
                          <button
                            onClick={() => handleDeleteWorkOrder(order)}
                            title="Purge (Global Administrator only)"
                            className="h-6 w-6 p-0 inline-flex items-center justify-center rounded hover:bg-red-100 transition-colors"
                          >
                            <Trash2 className="h-3 w-3" />
                          </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
                </table>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Preview Dialog */}
      <Dialog open={showPreviewDialog} onOpenChange={setShowPreviewDialog}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-xl">Work Order Preview</DialogTitle>
          </DialogHeader>
          {orderToPreview && (
            <div className="space-y-6 py-4">
              {/* TFS Work Order Number - Large, Bold, Red */}
              <div className="text-center border-b pb-4">
                <div className="text-sm text-gray-600 mb-1">TFS Work Order Number</div>
                <div className="text-4xl font-bold text-red-600">
                  {orderToPreview.tfs_work_order_number}
                </div>
              </div>

              {/* Job Information */}
              <div className="bg-gray-50 p-4 rounded-lg">
                <h3 className="font-bold text-lg mb-3 flex items-center">
                  <FileText className="h-5 w-5 mr-2" />
                  Job Information
                </h3>
                <div className="space-y-3">
                  <div className="grid grid-cols-3 gap-3">
                    <div>
                      <span className="text-sm text-gray-600">Job Name:</span>
                      <div className="font-medium">{humanizeDates(orderToPreview.job_name)}</div>
                    </div>
                    <div>
                      <span className="text-sm text-gray-600">Trade Type:</span>
                      <div className="font-medium">{orderToPreview.trade_type}</div>
                    </div>
                    <div>
                      <span className="text-sm text-gray-600">Priority Level:</span>
                      <div>
                        <Badge className={getPriorityColor(orderToPreview.priority_level)}>
                          {orderToPreview.priority_level}
                        </Badge>
                      </div>
                    </div>
                  </div>
                  
                  {/* Description of Work - Full Width */}
                  {orderToPreview.description_of_work && (
                    <div className="col-span-2 border-t pt-3">
                      <span className="text-sm text-gray-600">Description of Work Requested:</span>
                      <div className="font-medium whitespace-pre-wrap mt-1 bg-white p-2 rounded border">
                        {orderToPreview.description_of_work}
                      </div>
                    </div>
                  )}

                  <div className="grid grid-cols-2 gap-3 border-t pt-3">
                    {orderToPreview.work_order_received_by && (
                      <div>
                        <span className="text-sm text-gray-600">Received By:</span>
                        <div className="font-medium">{orderToPreview.work_order_received_by}</div>
                      </div>
                    )}
                    {orderToPreview.customer_work_order_number && (
                      <div>
                        <span className="text-sm text-gray-600">Customer WO #:</span>
                        <div className="font-medium">{orderToPreview.customer_work_order_number}</div>
                      </div>
                    )}
                    {orderToPreview.customer_po_number && (
                      <div>
                        <span className="text-sm text-gray-600">Customer PO #:</span>
                        <div className="font-medium">{orderToPreview.customer_po_number}</div>
                      </div>
                    )}
                    {orderToPreview.assigned_to_name && (
                      <div>
                        <span className="text-sm text-gray-600">Assigned To:</span>
                        <div className="font-medium text-red-600">{orderToPreview.assigned_to_name}</div>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Customer Information */}
              <div className="bg-gray-50 p-4 rounded-lg">
                <h3 className="font-bold text-lg mb-3">Customer Information</h3>
                <div className="grid grid-cols-2 gap-3">
                  <div className="col-span-2">
                    <span className="text-sm text-gray-600">Customer Name:</span>
                    <div className="font-medium text-lg">{orderToPreview.customer_name}</div>
                  </div>
                  
                  {/* Main Contact Fields */}
                  {(orderToPreview.main_contact_name || orderToPreview.main_contact_phone || orderToPreview.main_contact_email) && (
                    <>
                      <div className="col-span-2 border-t pt-2 mt-2">
                        <span className="text-xs font-semibold text-gray-700">Main Contact</span>
                      </div>
                      {orderToPreview.main_contact_name && (
                        <div>
                          <span className="text-sm text-gray-600">Name:</span>
                          <div className="font-medium">{orderToPreview.main_contact_name}</div>
                        </div>
                      )}
                      {orderToPreview.main_contact_phone && (
                        <div>
                          <span className="text-sm text-gray-600">Phone:</span>
                          <div className="font-medium">{orderToPreview.main_contact_phone}</div>
                        </div>
                      )}
                      {orderToPreview.main_contact_email && (
                        <div>
                          <span className="text-sm text-gray-600">Email:</span>
                          <div className="font-medium">{orderToPreview.main_contact_email}</div>
                        </div>
                      )}
                    </>
                  )}
                </div>
              </div>

              {/* Location Information */}
              <div className="bg-gray-50 p-4 rounded-lg">
                <h3 className="font-bold text-lg mb-3">Service Location</h3>
                <div className="space-y-2">
                  <div>
                    <span className="text-sm text-gray-600">Branch/Location:</span>
                    <div className="font-medium">{orderToPreview.service_location}</div>
                  </div>
                  {orderToPreview.service_address && (
                    <div>
                      <span className="text-sm text-gray-600">Full Address:</span>
                      <div className="font-medium">
                        {orderToPreview.service_address}
                        {orderToPreview.service_city && `, ${orderToPreview.service_city}`}
                        {orderToPreview.service_state && `, ${orderToPreview.service_state}`}
                        {orderToPreview.service_zip && ` ${orderToPreview.service_zip}`}
                      </div>
                    </div>
                  )}
                  {orderToPreview.service_zip && (
                    <div>
                      <span className="text-sm text-gray-600">ZIP Code:</span>
                      <div className="font-medium text-lg">{orderToPreview.service_zip}</div>
                    </div>
                  )}
                  {/* Local Contact */}
                  {(orderToPreview.local_rep_name || orderToPreview.local_rep_phone || orderToPreview.local_rep_email) && (
                    <div className="border-t pt-2 mt-2">
                      <span className="text-xs font-semibold text-gray-700 block mb-2">Local Point of Contact</span>
                      {orderToPreview.local_rep_name && (
                        <div className="mb-1">
                          <span className="text-sm text-gray-600">Name:</span>
                          <span className="font-medium ml-2">{orderToPreview.local_rep_name}</span>
                        </div>
                      )}
                      {orderToPreview.local_rep_phone && (
                        <div className="mb-1">
                          <span className="text-sm text-gray-600">Phone:</span>
                          <span className="font-medium ml-2">{orderToPreview.local_rep_phone}</span>
                        </div>
                      )}
                      {orderToPreview.local_rep_email && (
                        <div>
                          <span className="text-sm text-gray-600">Email:</span>
                          <span className="font-medium ml-2">{orderToPreview.local_rep_email}</span>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>

              {/* Timing Information */}
              <div className="bg-gray-50 p-4 rounded-lg">
                <h3 className="font-bold text-lg mb-3">Timing Information</h3>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <span className="text-sm text-gray-600">Date Received:</span>
                    <div className="font-medium">
                      {_fmtDate(orderToPreview.date_received)}
                    </div>
                  </div>
                  <div>
                    <span className="text-sm text-gray-600">Required Service Date:</span>
                    <div className="font-medium">
                      {_fmtDate(orderToPreview.required_service_date)}
                    </div>
                  </div>
                </div>
              </div>

              {/* Additional Instructions */}
              {(orderToPreview.local_contact_name || orderToPreview.special_instructions) && (
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h3 className="font-bold text-lg mb-3">Additional Information</h3>
                  {orderToPreview.local_contact_name && (
                    <div className="mb-3">
                      <span className="text-sm text-gray-600">Local Point of Contact:</span>
                      <div className="font-medium">
                        {orderToPreview.local_contact_name}
                        {orderToPreview.local_contact_phone && ` - ${orderToPreview.local_contact_phone}`}
                      </div>
                    </div>
                  )}
                  {orderToPreview.special_instructions && (
                    <div>
                      <span className="text-sm text-gray-600">Special Instructions:</span>
                      <div className="font-medium whitespace-pre-wrap mt-1">
                        {orderToPreview.special_instructions}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Discrete Billable Code */}
              <div className="flex items-center justify-end p-2 border-t">
                <span className="text-xs text-gray-500 mr-2">Billable Code:</span>
                <span className="text-xs font-mono font-semibold px-2 py-1 bg-gray-100 rounded">
                  {orderToPreview.billable ? "BWO" : "NB"}
                </span>
              </div>

              {/* Internal Notes - Admin Only */}
              <div className="bg-yellow-50 p-4 rounded-lg border border-yellow-200">
                <h3 className="font-bold text-lg mb-2 text-yellow-900">Internal Notes (Admin Only)</h3>
                <p className="text-xs text-yellow-700 mb-2">These notes are only visible to admins and will NOT appear in PDF or field portal</p>
                <textarea
                  className="w-full p-2 border rounded-md text-sm min-h-[80px]"
                  placeholder="Add internal notes here..."
                  value={internalNotes}
                  onChange={(e) => setInternalNotes(e.target.value)}
                  defaultValue={orderToPreview.internal_notes || ''}
                />
                <Button
                  size="sm"
                  onClick={handleSaveInternalNotes}
                  className="mt-2"
                >
                  Save Notes
                </Button>
              </div>
            </div>
          )}
          <DialogFooter className="flex justify-between items-center gap-4">
            <div className="flex space-x-2">
              {orderToPreview && orderToPreview.status !== 'Closed by Admin' && (
                <Button
                  variant="destructive"
                  onClick={handleCloseWorkOrder}
                >
                  Close Work Order
                </Button>
              )}
            </div>
            <div className="flex space-x-2">
              <Button
                variant="outline"
                onClick={() => handleDownloadPDF(orderToPreview)}
              >
                <Download className="h-4 w-4 mr-2" />
                Download PDF
              </Button>
              <Button variant="outline" onClick={() => setShowPreviewDialog(false)}>
                Close
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirm Delete Work Order</DialogTitle>
          </DialogHeader>
          <p>Are you sure you want to delete work order "{orderToDelete?.tfs_work_order_number}"? This action cannot be undone.</p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDeleteDialog(false)}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={confirmDelete}>
              Delete Work Order
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Close Work Order Confirmation Dialog */}
      <Dialog open={showCloseDialog} onOpenChange={setShowCloseDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Close Work Order</DialogTitle>
          </DialogHeader>
          <p>Are you sure you want to manually close work order "{orderToPreview?.tfs_work_order_number}"?</p>
          <p className="text-sm text-gray-600 mt-2">This will mark the work order as "Closed by Admin" and update the system records.</p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCloseDialog(false)}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={confirmCloseWorkOrder}>
              Close Work Order
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      </div>
    </div>
  );
};

export default AdminWorkOrderManagement;